import { computed, inject, Injectable, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { AppError } from '@/app/core/http/app-error.model';
import { toAppError } from '@/app/core/http/error-catalog';
import { buildPageMeta, DEFAULT_PAGE_SIZE, PageMeta } from '@/app/core/models/paged-result.model';
import { computePresetRange } from '@/app/shared/ui/components/date-range-selector/date-range-selector.logic';
import { PendingBonus, PendingBonusFilters } from '../models/pending-bonus.model';
import { PendingBonusRepository } from '../services/pending-bonus.repository';

export type PendingBonusesStatus = 'loading' | 'ready' | 'empty' | 'tooManyResults' | 'error';

const ERROR_SUBJECT = 'las cuentas habilitadas para emisión';

const TOO_MANY_RESULTS_STATUS = 422;

const emptyPage = (): PageMeta => buildPageMeta({ page: 0, size: DEFAULT_PAGE_SIZE }, 0);

export const defaultPendingBonusFilters = (): PendingBonusFilters =>
  computePresetRange('THIS_MONTH')!;

@Injectable()
export class PendingBonusesStore {
  private readonly repository = inject(PendingBonusRepository);

  private readonly _status = signal<PendingBonusesStatus>('loading');
  private readonly _records = signal<readonly PendingBonus[]>([]);
  private readonly _pageMeta = signal<PageMeta>(emptyPage());
  private readonly _filters = signal<PendingBonusFilters>(defaultPendingBonusFilters());
  private readonly _selected = signal<ReadonlySet<string>>(new Set());
  private readonly _size = signal(DEFAULT_PAGE_SIZE);
  private readonly _error = signal<AppError | null>(null);
  // Grilla completa cuando el BFF no pagina: las páginas se recorren en memoria.
  private allRecords: readonly PendingBonus[] | null = null;
  private inFlight?: Subscription;

  readonly status = this._status.asReadonly();
  readonly records = this._records.asReadonly();
  readonly pageMeta = this._pageMeta.asReadonly();
  readonly filters = this._filters.asReadonly();
  readonly selected = this._selected.asReadonly();
  readonly error = this._error.asReadonly();

  readonly isLoading = computed(() => this._status() === 'loading');
  readonly selectedCount = computed(() => this._selected().size);
  readonly allSelected = computed(() => {
    const records = this._records();
    const selected = this._selected();
    return records.length > 0 && records.every((record) => selected.has(record.folio));
  });

  reset(): void {
    this.inFlight?.unsubscribe();
    this.allRecords = null;
    this._filters.set(defaultPendingBonusFilters());
    this._selected.set(new Set());
    this._size.set(DEFAULT_PAGE_SIZE);
    this._records.set([]);
    this._pageMeta.set(emptyPage());
    this._error.set(null);
    this._status.set('loading');
  }

  search(filters: PendingBonusFilters): void {
    this._filters.set(filters);
    this.reload();
  }

  reload(): void {
    this._selected.set(new Set());
    this.allRecords = null;
    this.fetch(0);
  }

  changeSize(size: number): void {
    this._size.set(size);
    this.goToPage(0);
  }

  goToPage(page: number): void {
    if (this.allRecords) {
      this.showLocalPage(page);
      return;
    }
    this.fetch(page);
  }

  toggle(folio: string): void {
    this._selected.update((selected) => {
      const next = new Set(selected);
      if (next.has(folio)) next.delete(folio);
      else next.add(folio);
      return next;
    });
  }

  toggleAll(): void {
    const folios = this._records().map((record) => record.folio);
    const select = !this.allSelected();
    this._selected.update((selected) => {
      const next = new Set(selected);
      folios.forEach((folio) => (select ? next.add(folio) : next.delete(folio)));
      return next;
    });
  }

  private fetch(page: number): void {
    this.inFlight?.unsubscribe();
    this._error.set(null);
    this._status.set('loading');

    this.inFlight = this.repository
      .search(this._filters(), { page, size: this._size() })
      .subscribe({
        next: (result) => {
          if (result.page) {
            this.allRecords = null;
            this.show(result.records, result.page);
          } else {
            this.allRecords = result.records;
            this.showLocalPage(0);
          }
        },
        error: (cause: unknown) => {
          this.allRecords = null;
          this._records.set([]);
          this._pageMeta.set(emptyPage());
          if (cause instanceof HttpErrorResponse && cause.status === TOO_MANY_RESULTS_STATUS) {
            this._status.set('tooManyResults');
            return;
          }
          this._error.set(toAppError(cause, ERROR_SUBJECT));
          this._status.set('error');
        },
      });
  }

  private showLocalPage(page: number): void {
    const all = this.allRecords ?? [];
    const size = this._size();
    const from = page * size;
    this.show(all.slice(from, from + size), buildPageMeta({ page, size }, all.length));
  }

  private show(records: readonly PendingBonus[], page: PageMeta): void {
    this._records.set(records);
    this._pageMeta.set(page);
    this._status.set(page.totalElements === 0 ? 'empty' : 'ready');
  }
}
